import Heading from '@ramsey-design-system/heading';
import React from 'react';
import { ActionGroup } from '../../types';
import ActionItems from '../ActionItems/ActionItems';
import styles from './lesson-outline.module.css';

type LessonOutlineProps = {
  title?: string;
  actionGroups: ActionGroup[];
  lessonNumber?: string;
  urlPrefix?: string;
  compact?: boolean;
  currentActionItemId?: string;
  collapsedActionGroupId?: string;
};

const defaultProps = {
  urlPrefix: '/learn',
  compact: false,
};

function renderActionGroups({
  actionGroups,
  lessonNumber,
  urlPrefix,
  currentActionItemId,
  compact,
  collapsedActionGroupId,
}: LessonOutlineProps) {
  return actionGroups.map((actionGroup, index, allItems) => {
    const showCurrentActionItem = actionGroup.action_items.some(
      actionItem => actionItem.id === currentActionItemId
    );
    const isLastActionGroup = allItems.length === index + 1;
    const collapse = actionGroup.id === collapsedActionGroupId;

    return (
      <ActionItems
        key={index}
        actionGroup={actionGroup}
        lessonNumber={lessonNumber}
        urlPrefix={urlPrefix}
        showContent={false}
        showLessonTag={false}
        showTitle={false}
        showCurrentActionItem={showCurrentActionItem}
        currentActionItemId={currentActionItemId}
        showStepper
        compact={compact}
        className={styles.action_item_container}
        collapse={collapse}
        isLastActionGroup={isLastActionGroup}
        chevronRightIcon={true}
      />
    );
  });
}

/**
 * Renders a lesson outline, for all of the specified action groups. Renders a single 'collapsed' item if it
 * has a `multi-survey` actionGroupId.
 *
 * @param param0
 * @returns
 */
function LessonOutline({
  lessonNumber,
  title,
  actionGroups,
  urlPrefix,
  compact,
  currentActionItemId,
  collapsedActionGroupId = 'multi-survey',
}: LessonOutlineProps) {
  const containerClassNames = compact
    ? `${styles.container} ${styles.compact} LessonOutline`
    : `${styles.container} LessonOutline`;

  return (
    <div className={containerClassNames} data-testid="lesson-outline">
      <div className={styles.header}>
        {lessonNumber && (
          <h3 className={styles.lesson} data-testid="lesson-outline-number">
            {lessonNumber}
          </h3>
        )}
        <Heading level="2" data-testid="lesson-outline-title">
          {title}
        </Heading>
      </div>

      {renderActionGroups({
        actionGroups,
        lessonNumber,
        urlPrefix,
        currentActionItemId,
        compact,
        collapsedActionGroupId,
      })}
    </div>
  );
}

LessonOutline.defaultProps = defaultProps;

export default LessonOutline;
